import React, { useState, useEffect } from 'react';
import { pipelineService, Pipeline } from '../../services/pipelineService';
import { usePipelineBuilder } from '../../context/PipelineBuilderContext';
import './PipelineBrowser.css';

interface PipelineBrowserProps {
  isOpen: boolean;
  onClose: () => void;
}

const PipelineBrowser: React.FC<PipelineBrowserProps> = ({ isOpen, onClose }) => {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { loadPipeline } = usePipelineBuilder();

  useEffect(() => {
    if (isOpen) {
      loadPipelines();
    }
  }, [isOpen]);

  const loadPipelines = async () => {
    setLoading(true);
    setError(null);
    try {
      const pipelineList = await pipelineService.listPipelines();
      setPipelines(pipelineList);
    } catch (err: any) {
      setError(err.message || 'Failed to load pipelines');
      console.error('Error loading pipelines:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLoadPipeline = async (pipeline: Pipeline) => {
    try {
      setLoading(true);
      const { pipeline_json } = await pipelineService.getPipeline(pipeline.id);
      
      if (pipeline_json) {
        loadPipeline(pipeline_json);
        onClose();
      } else {
        alert('Pipeline configuration not found');
      }
    } catch (err: any) {
      alert(`Failed to load pipeline: ${err.message || 'Unknown error'}`);
      console.error('Error loading pipeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePipeline = async (pipeline: Pipeline, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!window.confirm(`Are you sure you want to delete "${pipeline.display_name || pipeline.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await pipelineService.deletePipeline(pipeline.id);
      // Reload pipelines list
      await loadPipelines();
    } catch (err: any) {
      alert(`Failed to delete pipeline: ${err.message || 'Unknown error'}`);
      console.error('Error deleting pipeline:', err);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Unknown';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pipeline-browser-overlay" onClick={onClose}>
      <div className="pipeline-browser-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pipeline-browser-header">
          <h2>📂 Saved Pipelines</h2>
          <button className="pipeline-browser-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="pipeline-browser-content">
          {loading && pipelines.length === 0 ? (
            <div className="pipeline-browser-loading">
              <p>Loading pipelines...</p>
            </div>
          ) : error ? (
            <div className="pipeline-browser-error">
              <p>Error: {error}</p>
              <button onClick={loadPipelines}>Retry</button>
            </div>
          ) : pipelines.length === 0 ? (
            <div className="pipeline-browser-empty">
              <p>No saved pipelines found.</p>
              <p className="pipeline-browser-empty-hint">
                Create a pipeline in the Pipeline Builder and save it to see it here.
              </p>
            </div>
          ) : (
            <div className="pipeline-browser-grid">
              {pipelines.map((pipeline) => (
                <div key={pipeline.id} className="pipeline-browser-card">
                  <div className="pipeline-browser-card-header">
                    <h3>{pipeline.display_name || pipeline.name}</h3>
                    <button
                      className="pipeline-browser-delete-btn"
                      onClick={(e) => handleDeletePipeline(pipeline, e)}
                      title="Delete pipeline"
                    >
                      🗑️
                    </button>
                  </div>
                  {pipeline.description && (
                    <p className="pipeline-browser-card-description">{pipeline.description}</p>
                  )}
                  <div className="pipeline-browser-card-meta">
                    <span className="pipeline-browser-card-date">
                      Created: {formatDate(pipeline.created_at)}
                    </span>
                  </div>
                  <button
                    className="pipeline-browser-load-btn"
                    onClick={() => handleLoadPipeline(pipeline)}
                    disabled={loading}
                  >
                    📥 Load Pipeline
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PipelineBrowser;

